import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { Portal } from './Portal';
import './Toast.css';

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

/** A single follow-up the toast offers, for outcomes that continue elsewhere. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  tone?: ToastTone;
  title: string;
  description?: string;
  /** Where to go next when the work the toast announces is not finished — e.g. an
   *  upload that is still being indexed on another tab. */
  action?: ToastAction;
  /** Auto-dismiss delay in ms. 0 keeps it until dismissed. Defaults to 5000 —
   *  or to 0 when the toast carries an {@link action}, since a follow-up that
   *  expires after five seconds is a follow-up most people never get to offer. */
  durationMs?: number;
}

interface ToastRecord extends Required<Pick<ToastOptions, 'title'>> {
  id: string;
  tone: ToastTone;
  description?: string;
  action?: ToastAction;
  durationMs: number;
}

interface ToastContextValue {
  /** Show a toast; returns its id so callers can dismiss it early. */
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 aria-hidden="true" />,
  error: <XCircle aria-hidden="true" />,
  warning: <AlertTriangle aria-hidden="true" />,
  info: <Info aria-hidden="true" />,
};

/**
 * Transient feedback, top-right, semantic and auto-dismissing. Timers pause on
 * hover so a message is never lost mid-read. Announced via an aria-live region.
 * Mount once near the app root inside the provider tree.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timers = useRef(new Map<string, number>());

  const clearTimer = useCallback((id: string) => {
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    },
    [clearTimer],
  );

  const scheduleDismiss = useCallback(
    (id: string, durationMs: number) => {
      if (durationMs <= 0) return;
      clearTimer(id);
      const handle = window.setTimeout(() => dismiss(id), durationMs);
      timers.current.set(id, handle);
    },
    [clearTimer, dismiss],
  );

  const toast = useCallback(
    ({ tone = 'info', title, description, action, durationMs }: ToastOptions): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      // A toast that offers a follow-up is not just feedback, it is the only route
      // to the place the work continues — so it waits for the person instead of
      // timing out while they finish reading it.
      const duration = durationMs ?? (action ? 0 : 5000);
      setToasts((prev) => [...prev, { id, tone, title, description, action, durationMs: duration }]);
      scheduleDismiss(id, duration);
      return id;
    },
    [scheduleDismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((handle) => window.clearTimeout(handle));
      map.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Portal>
        <div className="toast-region" role="region" aria-label="Notificações">
          <ol className="toast-list" aria-live="polite">
            {toasts.map((t) => (
              <ToastItem
                key={t.id}
                toast={t}
                onDismiss={() => dismiss(t.id)}
                onHold={() => clearTimer(t.id)}
                onRelease={() => scheduleDismiss(t.id, t.durationMs)}
              />
            ))}
          </ol>
        </div>
      </Portal>
    </ToastContext.Provider>
  );
}

interface ToastItemProps {
  toast: ToastRecord;
  onDismiss: () => void;
  /** Freeze the auto-dismiss timer: the person is reading or focused inside. */
  onHold: () => void;
  /** Resume it. */
  onRelease: () => void;
}

function ToastItem({ toast, onDismiss, onHold, onRelease }: ToastItemProps): JSX.Element {
  return (
    <li
      className={`toast toast--${toast.tone}`}
      onMouseEnter={onHold}
      onMouseLeave={onRelease}
      // Keyboard parity with the hover pause: React's onFocus/onBlur are the
      // bubbling focusin/focusout, so tabbing INTO the action or the close button
      // holds the timer exactly like the pointer does — without it, reaching the
      // follow-up by keyboard could dismiss the toast from under the caret.
      onFocus={onHold}
      onBlur={onRelease}
    >
      <span className="toast__icon">{ICONS[toast.tone]}</span>
      <div className="toast__body">
        <p className="toast__title">{toast.title}</p>
        {toast.description && <p className="toast__description">{toast.description}</p>}
        {toast.action && (
          <button
            type="button"
            className="toast__action"
            onClick={() => {
              toast.action?.onClick();
              onDismiss();
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button type="button" className="toast__close" onClick={onDismiss} aria-label="Dispensar">
        <X aria-hidden="true" />
      </button>
    </li>
  );
}

/** Access the toast dispatcher. Throws outside {@link ToastProvider}. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider.');
  }
  return ctx;
}
